import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { ChartTooltip } from './ChartTooltip';

// Word-wrap a string into lines of at most `maxChars` characters (never truncates).
const wrap = (text, maxChars) => {
  if (text === null || text === undefined || text === '') return [];
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (t.length > maxChars && cur) { lines.push(cur); cur = w; }
    else cur = t;
  }
  if (cur) lines.push(cur);
  return lines;
};

// Multi-line Y-axis tick: bold main label + optional small sub-lines
// (e.g. canal / région). Everything wraps — nothing is ever truncated.
const makeTick = (subLabels, tickFormatter, yWidth) => (props) => {
  const { x, y, payload } = props;
  const main = tickFormatter ? tickFormatter(payload.value) : payload.value;
  const mainMax = Math.max(10, Math.floor(yWidth / 6.2));
  const subMax = Math.max(12, Math.floor(yWidth / 4.8));
  const mainLines = wrap(String(main), mainMax);
  const subs = subLabels?.[payload.value] || [];
  const subLines = [];
  subs.forEach((s) => wrap(s, subMax).forEach((l) => subLines.push(l)));
  const lh = 12;
  const total = mainLines.length + subLines.length;
  const startY = -((total - 1) * lh) / 2;
  return (
    <g transform={`translate(${x},${y})`}>
      {mainLines.map((l, i) => (
        <text key={`m${i}`} x={-6} y={startY + i * lh} dy={4} textAnchor="end"
          style={{ fontSize: 11, fontWeight: 600, fill: 'hsl(var(--foreground))' }}>{l}</text>
      ))}
      {subLines.map((l, i) => (
        <text key={`s${i}`} x={-6} y={startY + (mainLines.length + i) * lh} dy={4} textAnchor="end"
          style={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}>{l}</text>
      ))}
    </g>
  );
};

export const HorizontalBars = ({ data = [], dataKey = 'value', labelKey = 'key', height = 300, color, formatTip, highlightTop = true, yWidth = 110, tickFormatter, subLabels }) => {
  const base = color || 'var(--tenant-accent-500)';
  const multiline = !!(subLabels || tickFormatter) || data.some((d) => String(d[labelKey] || '').length > Math.floor(yWidth / 6.2));
  const tick = multiline ? makeTick(subLabels, tickFormatter, yWidth) : { fontSize: 11 };
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(11,19,32,0.06)" horizontal={false} />
          <XAxis type="number" tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey={labelKey} tickLine={false} axisLine={false} width={yWidth}
                 interval={0} tick={tick} />
          <Tooltip cursor={{ fill: 'rgba(11,19,32,0.04)' }} content={<ChartTooltip formatter={formatTip} />} />
          <Bar dataKey={dataKey} radius={[0, 6, 6, 0]} maxBarSize={26}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color || (highlightTop && i === 0 ? base : 'var(--tenant-accent-100)')} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
